import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { rolesFor, PROFESSIONAL_ROLES } from '../../lib/orgTypes';

/**
 * "Add someone" — a person who worked with you at this organisation. Roles
 * for this kind of organisation first, then every other role, so a school
 * nurse or a council's family support worker can still be named.
 */
export const AddPersonDialog = ({ open, onOpenChange, org, onAdd, saving }) => {
  const own = rolesFor(org.type);
  const others = Object.values(PROFESSIONAL_ROLES).flat()
    .filter((r, i, all) => !own.some((o) => o.value === r.value) && all.findIndex((x) => x.value === r.value) === i);
  const [person, setPerson] = useState({ name: '', role: own[0]?.value || 'social_worker', dates_known: '' });
  const submit = async () => {
    const payload = { name: person.name.trim(), role: person.role, ...(person.dates_known.trim() ? { dates_known: person.dates_known.trim() } : {}) };
    if (await onAdd(payload)) {
      setPerson({ name: '', role: own[0]?.value || 'social_worker', dates_known: '' });
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-person-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">Add someone</DialogTitle>
          <DialogDescription className="text-ink-55">Someone who worked with you at {org.name}. Names help them find your records.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold" htmlFor="add-person-name">What was their name?</Label>
            <Input id="add-person-name" value={person.name} onChange={(e) => setPerson({ ...person, name: e.target.value })} placeholder="Full name, or what you called them" data-testid="add-person-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold">What was their role?</Label>
            <Select value={person.role} onValueChange={(v) => setPerson({ ...person, role: v })}>
              <SelectTrigger data-testid="add-person-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...own, ...others].map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold" htmlFor="add-person-when">When did you know them?</Label>
            <p className="text-sm text-ink-55">Rough is fine — “2012 to 2014” or “around Year 9”.</p>
            <Input id="add-person-when" value={person.dates_known} onChange={(e) => setPerson({ ...person, dates_known: e.target.value })} data-testid="add-person-when" />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!person.name.trim() || saving} data-testid="add-person-submit">{saving ? 'Adding' : 'Add person'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPersonDialog;
